import { useEffect, useState } from "react";
import { api } from "./api";
import { Btn, Field, TextArea, TextInput, useSaveState } from "./atoms";
import { useAdminAuth } from "./AuthContext";

function ChangePasswordCard() {
  const auth = useAdminAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async () => {
    setMsg("");
    setErr("");
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(cur, next);
      setMsg("Password updated. Use it on your next sign-in.");
      setCur("");
      setNext("");
      setConfirm("");
    } catch (e) {
      setErr(e.message || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  // Live client-side validation surface so the inline error appears the
  // moment the user creates a mismatch — not only after they click submit.
  const liveErr =
    next && confirm && next !== confirm
      ? "New passwords do not match."
      : next && next.length > 0 && next.length < 8
      ? "New password must be at least 8 characters."
      : "";
  const displayErr = err || liveErr;

  return (
    <div data-testid="account-card" className="mt-12 p-6 rounded-xl border border-white/[0.08] bg-[#0F141A]">
      <div className="flex items-center justify-between mb-1">
        <div>
          <div className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">Account</div>
          <h2 className="mt-2 font-display text-xl font-black tracking-[-0.02em]">Change password</h2>
        </div>
        <div className="text-right">
          <div className="text-[0.62rem] tracking-[0.22em] uppercase text-neutral-500">Signed in as</div>
          <div className="text-sm text-white font-bold mt-1" data-testid="account-email">{auth.user?.email}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-4 mt-5">
        <Field label="Current password">
          <input
            type="password"
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            data-testid="pw-current"
            className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors"
          />
        </Field>
        <Field label="New password" hint="At least 8 characters">
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="pw-new"
            className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors"
          />
        </Field>
        <Field label="Confirm new password">
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="pw-confirm"
            className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors"
          />
        </Field>
      </div>
      <div className="mt-5 flex items-center gap-4">
        <Btn onClick={submit} disabled={busy || !cur || !next || !confirm} data-testid="pw-save">
          {busy ? "Updating…" : "Update password"}
        </Btn>
        {msg ? <span className="text-[0.72rem] text-[#F4D64E]" data-testid="settings-pw-msg">{msg}</span> : null}
        {displayErr ? (
          <span
            className="text-[0.72rem] text-red-400"
            data-testid={
              displayErr === "New passwords do not match."
                ? "settings-pw-mismatch"
                : displayErr === "New password must be at least 8 characters."
                ? "settings-pw-short"
                : "settings-pw-err"
            }
          >
            {displayErr}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const { saving, saved, error, wrap } = useSaveState();

  useEffect(() => { api.settingsGet().then(setSettings); }, []);

  if (!settings) return <div className="text-sm text-neutral-500">Loading…</div>;

  const set = (k, v) => setSettings({ ...settings, [k]: v });
  const setSocial = (k, v) =>
    setSettings({ ...settings, social: { ...(settings.social || {}), [k]: v } });
  const setDefSeo = (k, v) =>
    setSettings({ ...settings, default_seo: { ...(settings.default_seo || {}), [k]: v } });
  const setFooter = (k, v) =>
    setSettings({ ...settings, footer: { ...(settings.footer || {}), [k]: v } });

  const save = () => wrap(() => api.settingsUpdate(settings));

  return (
    <div data-testid="admin-settings" className="pb-20">
      <div className="flex items-center justify-between mb-9">
        <div>
          <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">Site</div>
          <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.025em]">Global settings</h1>
        </div>
        <div className="flex items-center gap-3">
          {saved ? <span className="text-[0.7rem] text-[#F4D64E]">Saved ✓</span> : null}
          {error ? <span className="text-[0.7rem] text-red-400">{error}</span> : null}
          <Btn onClick={save} disabled={saving} data-testid="settings-save">
            {saving ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <Field label="Site name"><TextInput value={settings.site_name} onChange={(v) => set("site_name", v)} /></Field>
        <Field label="Tagline"><TextInput value={settings.tagline} onChange={(v) => set("tagline", v)} /></Field>
        <Field label="Logo URL"><TextInput value={settings.logo_url} onChange={(v) => set("logo_url", v)} /></Field>
        <Field label="Favicon URL"><TextInput value={settings.favicon_url} onChange={(v) => set("favicon_url", v)} /></Field>
        <Field label="Contact email"><TextInput value={settings.contact_email} onChange={(v) => set("contact_email", v)} /></Field>
        <Field label="Form recipient email"><TextInput value={settings.form_recipient_email} onChange={(v) => set("form_recipient_email", v)} /></Field>
      </div>

      <h2 className="mt-12 mb-4 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">Social links</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
        {["instagram", "linkedin", "dribbble", "twitter", "tiktok", "behance"].map((k) => (
          <Field key={k} label={k}><TextInput value={settings.social?.[k]} onChange={(v) => setSocial(k, v)} /></Field>
        ))}
      </div>

      <h2 className="mt-12 mb-4 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">Default SEO</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
        <Field label="Default meta title"><TextInput value={settings.default_seo?.title} onChange={(v) => setDefSeo("title", v)} /></Field>
        <Field label="Default OG image URL"><TextInput value={settings.default_seo?.og_image} onChange={(v) => setDefSeo("og_image", v)} /></Field>
        <div className="lg:col-span-2">
          <Field label="Default meta description"><TextArea value={settings.default_seo?.description} onChange={(v) => setDefSeo("description", v)} rows={3} /></Field>
        </div>
      </div>

      <h2 className="mt-12 mb-4 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">Footer</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
        <Field label="Address line"><TextInput value={settings.footer?.address} onChange={(v) => setFooter("address", v)} /></Field>
        <Field label="Copyright line"><TextInput value={settings.footer?.copyright} onChange={(v) => setFooter("copyright", v)} /></Field>
      </div>

      <ChangePasswordCard />
    </div>
  );
}
